import * as THREE from "three";
import type { SecondaryResponse } from "../physics";
import type { CharacterRig } from "./class-kits";

export const COURT_WARRIOR_ITEMS = [
  "armor.warrior.breastplate",
  "armor.warrior.helmet",
  "armor.warrior.pauldrons",
  "armor.warrior.gauntlets",
  "armor.warrior.greaves",
  "weapon.arming-sword",
  "offhand.kite-shield",
  "back.short-cape",
  "crest.plume",
] as const;

export const STUDIO_LOADOUTS = {
  full: [...COURT_WARRIOR_ITEMS],
  light: [
    "armor.warrior.breastplate",
    "armor.warrior.gauntlets",
    "weapon.arming-sword",
    "back.short-cape",
  ],
  sentinel: [
    "armor.warrior.helmet",
    "armor.warrior.pauldrons",
    "armor.warrior.greaves",
    "offhand.kite-shield",
    "crest.plume",
  ],
  base: [],
} as const satisfies Record<string, readonly string[]>;

type EquipmentBuilder = (rig: CharacterRig) => void;

function bindSecondary(
  rig: CharacterRig,
  id: string,
  object: THREE.Object3D,
  response: SecondaryResponse,
  additiveToPose = false,
  rotationScale = 1,
): void {
  rig.secondaryBodies.push({
    id,
    object,
    response,
    restRotation: { x: object.rotation.x, y: object.rotation.y, z: object.rotation.z },
    additiveToPose,
    rotationScale,
  });
}

const equipment = new Map<string, EquipmentBuilder>([
  ["armor.warrior.breastplate", (rig) => {
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.48, 0.14), rig.materials.accent);
    plate.position.set(0, 0.05, 0.25);
    plate.rotation.x = -0.08;
    rig.torso.add(plate);
    const belt = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.1, 0.32), rig.materials.dark);
    belt.position.y = -0.42;
    rig.torso.add(belt);
  }],
  ["armor.warrior.helmet", (rig) => {
    const helmet = new THREE.Mesh(new THREE.IcosahedronGeometry(0.21, 0), rig.materials.body);
    helmet.scale.set(0.92, 1.04, 0.96);
    rig.head.add(helmet);
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.29, 0.08, 0.07), rig.materials.accent);
    visor.position.set(0, -0.01, 0.18);
    rig.head.add(visor);
    const noseGuard = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.19, 0.05), rig.materials.accent);
    noseGuard.position.set(0, -0.09, 0.2);
    rig.head.add(noseGuard);
  }],
  ["crest.plume", (rig) => {
    const plumeRoot = new THREE.Group();
    plumeRoot.name = "secondary.crest.plume";
    plumeRoot.position.set(0, 0.24, -0.03);
    plumeRoot.rotation.x = -0.18;
    const plume = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.42, 0.12), rig.materials.accent);
    plume.position.y = 0.2;
    plumeRoot.add(plume);
    rig.head.add(plumeRoot);
    bindSecondary(rig, "crest.plume", plumeRoot, {
      stiffness: 24, damping: 5.8, windAffinity: 1.4, impulseAffinity: 1.35, limit: 0.22,
    }, false, 2.4);
  }],
  ["armor.warrior.pauldrons", (rig) => {
    for (const arm of [rig.leftArm, rig.rightArm]) {
      const pauldron = new THREE.Mesh(new THREE.IcosahedronGeometry(0.17, 0), rig.materials.accent);
      pauldron.position.set(0, -0.04, 0);
      pauldron.scale.set(1.35, 0.68, 1);
      arm.add(pauldron);
    }
  }],
  ["armor.warrior.gauntlets", (rig) => {
    for (const hand of [rig.leftHand, rig.rightHand]) {
      const gauntlet = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.19, 0.15), rig.materials.dark);
      gauntlet.position.y = -0.04;
      hand.add(gauntlet);
    }
  }],
  ["armor.warrior.greaves", (rig) => {
    for (const shin of [rig.leftShin, rig.rightShin]) {
      const greave = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.45, 0.16), rig.materials.body);
      greave.position.set(0, -0.23, 0.025);
      shin.add(greave);
    }
  }],
  ["weapon.arming-sword", (rig) => {
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.34, 0.07), rig.materials.dark);
    grip.position.y = -0.14;
    rig.weapon.add(grip);
    const crossguard = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.07, 0.09), rig.materials.accent);
    crossguard.position.y = -0.34;
    rig.weapon.add(crossguard);
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.92, 0.055), rig.materials.accent);
    blade.position.y = -0.82;
    rig.weapon.add(blade);
    const point = new THREE.Mesh(new THREE.ConeGeometry(0.095, 0.22, 4), rig.materials.accent);
    point.position.y = -1.39;
    point.rotation.z = Math.PI;
    rig.weapon.add(point);
    bindSecondary(rig, "weapon.arming-sword", rig.weapon, {
      stiffness: 34, damping: 8.5, windAffinity: 0.015, impulseAffinity: 0.42, limit: 0.075,
    }, true, 0.8);
  }],
  ["offhand.kite-shield", (rig) => {
    const shield = new THREE.Mesh(new THREE.CylinderGeometry(0.27, 0.36, 0.1, 6), rig.materials.body);
    shield.rotation.z = Math.PI / 2;
    shield.scale.set(1, 1.2, 1);
    rig.guard.add(shield);
    const face = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.54, 0.13), rig.materials.accent);
    face.position.x = 0.075;
    face.rotation.z = Math.PI / 2;
    rig.guard.add(face);
    const boss = new THREE.Mesh(new THREE.OctahedronGeometry(0.11, 0), rig.materials.accent);
    boss.position.x = 0.105;
    rig.guard.add(boss);
    bindSecondary(rig, "offhand.kite-shield", rig.guard, {
      stiffness: 38, damping: 9.5, windAffinity: 0.01, impulseAffinity: 0.32, limit: 0.055,
    }, true, 0.65);
  }],
  ["back.short-cape", (rig) => {
    const capeRoot = new THREE.Group();
    capeRoot.name = "secondary.back.short-cape";
    capeRoot.position.set(0, 2.2, -0.27);
    capeRoot.rotation.x = -0.1;
    const cape = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.92, 0.055), rig.materials.dark);
    cape.position.y = -0.46;
    capeRoot.add(cape);
    rig.modelRoot.add(capeRoot);
    bindSecondary(rig, "back.short-cape", capeRoot, {
      stiffness: 12, damping: 4.2, windAffinity: 1.2, impulseAffinity: 1.65, limit: 0.32,
    }, false, 2.4);
  }],
]);

export function equipHumanoid(rig: CharacterRig, itemIds: readonly string[]): string[] {
  const equipped: string[] = [];
  for (const id of itemIds) {
    const build = equipment.get(id);
    if (!build) continue;
    build(rig);
    equipped.push(id);
  }
  return equipped;
}
